import { useEffect } from 'react'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { Analytics } from '@vercel/analytics/react'
import { TooltipProvider } from '@/components/ui/tooltip'
import Navbar from '@/components/navbar'
import Footer from '@/components/footer'
import ScrollToTop from '@/components/scroll-to-top'
import PageTransition from '@/components/page-transition'
import StartupScreen from '@/components/StartupScreen'
import HomePage from '@/pages/Home'
import AboutPage from '@/pages/About'
import ProjectsPage from '@/pages/Projects'
import ContactPage from '@/pages/Contact'
import BlogPage from '@/pages/Blog'
import BlogPostPage from '@/pages/BlogPost'
import ResumePage from '@/pages/Resume'
import NotFoundPage from '@/pages/NotFound'
import ArduinoConnectPage from '@/pages/ArduinoConnect'

function AnimatedRoutes() {
    const location = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
        const hostname = window.location.hostname
        if (location.pathname === '/') {
            if (hostname.startsWith('blog.')) {
                navigate('/blog', { replace: true })
            } else if (hostname.startsWith('resume.')) {
                navigate('/resume', { replace: true })
            } else if (hostname.startsWith('projects.')) {
                navigate('/projects', { replace: true })
            } else if (hostname.startsWith('connect.')) {
                navigate('/connect', { replace: true })
            }
        }
    }, [location.pathname, navigate])

    return (
        <AnimatePresence mode="wait" initial={false}>
            <Routes location={location} key={location.pathname}>
                <Route path="/"       element={<PageTransition><HomePage /></PageTransition>} />
                <Route path="/about"  element={<PageTransition><AboutPage /></PageTransition>} />
                <Route path="/projects" element={<PageTransition><ProjectsPage /></PageTransition>} />
                <Route path="/contact"  element={<PageTransition><ContactPage /></PageTransition>} />
                <Route path="/blog"     element={<PageTransition><BlogPage /></PageTransition>} />
                <Route path="/blog/:slug" element={<PageTransition><BlogPostPage /></PageTransition>} />
                <Route path="/resume"   element={<PageTransition><ResumePage /></PageTransition>} />
                <Route path="/connect"  element={<PageTransition><ArduinoConnectPage /></PageTransition>} />
                <Route path="*"         element={<PageTransition><NotFoundPage /></PageTransition>} />
            </Routes>
        </AnimatePresence>
    )
}

function App() {
    return (
        <TooltipProvider delayDuration={0}>
            {/* Vercel Analytics tracking */}
            <Analytics />

            {/* Boot sequence overlay — renders once per session */}
            <StartupScreen />

            {/* Pure black terminal base */}
            <div className="relative min-h-screen bg-background text-foreground font-mono">
                {/* Always-on faint green scanline texture over the entire viewport */}
                <div className="global-scanlines" aria-hidden="true" />

                {/* Faint dot grid background — fixed, behind everything */}
                <div className="fixed inset-0 -z-10 terminal-grid opacity-60" />

                {/* Page content */}
                <div className="relative flex flex-col min-h-screen">
                    <Navbar />
                    <main className="flex-1 pb-0">
                        <ScrollToTop />
                        <AnimatedRoutes />
                    </main>
                    <Footer />
                </div>
            </div>
        </TooltipProvider>
    )
}

export default App
